import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AirVent, Box, WashingMachine, Tv, Droplets, Wrench, MapPin } from 'lucide-react';
import { services } from '../../data/services';
import { brands } from '../../data/brands';
import LocationSelector from '../booking/LocationSelector';

interface LocationServicesProps {
  city: string;
  state: string;
  area?: string;
  title: string;
}

export default function LocationServices({ city, state, area, title }: LocationServicesProps) {
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedCity, setSelectedCity] = useState(city || '');
  const [selectedArea, setSelectedArea] = useState(area || '');

  const getServiceIcon = (iconName: string) => {
    const icons = {
      AirVent,
      Tv,
      WashingMachine,
      Box,
      Droplets
    };
    return icons[iconName as keyof typeof icons] || Box;
  };

  const getServiceLink = (serviceId: string) => {
    if (area) {
      return `/locations/${state.toLowerCase()}/${city.toLowerCase()}/area/${area}`;
    }
    if (city) {
      return `/locations/${state.toLowerCase()}/${city.toLowerCase()}/${serviceId}`;
    }
    return `/locations/${state.toLowerCase()}/service/${serviceId}`;
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {title}
          </h2>
          <p className="text-lg text-gray-600">
            Professional repair services for all your home appliances
          </p>
        </div>

        {/* Selection Panel */}
        <div className="max-w-3xl mx-auto mb-12">
          <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
            {/* Brand Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Brand
              </label>
              <select
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Brands</option>
                {brands.map((brand) => (
                  <option key={brand.id} value={brand.id}>
                    {brand.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Location Selection */}
            <LocationSelector
              state={state}
              city={selectedCity}
              area={selectedArea}
              onStateChange={() => {}} // State is fixed from URL
              onCityChange={setSelectedCity}
              onAreaChange={setSelectedArea}
              hideState={true} // Hide state selection since we're already in a state context
            />
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => {
            const Icon = getServiceIcon(service.icon);
            const serviceLink = getServiceLink(service.id);
            
            return (
              <Link
                key={service.id}
                to={serviceLink}
                className="group bg-white p-6 rounded-xl border border-gray-200 hover:border-blue-500 transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 p-3 bg-blue-50 rounded-lg group-hover:bg-blue-100">
                    <Icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600">
                      {selectedBrand ? `${brands.find(b => b.id === selectedBrand)?.name} ` : ''}
                      {service.name}
                    </h3>
                    <p className="mt-2 text-gray-600">
                      {service.description}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <span className="text-sm bg-blue-50 text-blue-700 px-2 py-1 rounded">
                        90-Day Warranty
                      </span>
                      <span className="text-sm bg-green-50 text-green-700 px-2 py-1 rounded">
                        Same Day Service
                      </span>
                    </div>
                    {selectedCity && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <p className="text-sm text-gray-600">
                          Available in {selectedArea && `${selectedArea}, `}{selectedCity}, {state}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}